import type { DashboardPayload, RoadmapItem } from "../types/dashboard";

type RelativeRoadmapItem = Omit<RoadmapItem, "startDate" | "endDate"> & {
  startOffsetDays: number;
  endOffsetDays: number;
  startTime: string;
  endTime: string;
};

const padDatePart = (value: number) => value.toString().padStart(2, "0");

const toRelativeDateTime = (
  baseDate: Date,
  offsetDays: number,
  time: string,
) => {
  const [hour, minute] = time.split(":").map(Number);
  const nextDate = new Date(baseDate);

  nextDate.setDate(nextDate.getDate() + offsetDays);
  nextDate.setHours(hour, minute, 0, 0);

  return (
    [
      nextDate.getFullYear(),
      padDatePart(nextDate.getMonth() + 1),
      padDatePart(nextDate.getDate()),
    ].join("-") +
    `T${padDatePart(nextDate.getHours())}:${padDatePart(nextDate.getMinutes())}`
  );
};

// Keep dummy roadmap items anchored near today so the timeline marker remains useful.
export const buildRoadmapItems = (baseDate = new Date()): RoadmapItem[] => {
  const items: RelativeRoadmapItem[] = [
    {
      id: 1,
      group: 1,
      title: "ECharts wrapper",
      startOffsetDays: -28,
      endOffsetDays: -18,
      startTime: "09:30",
      endTime: "18:00",
      progress: 100,
      status: "done",
      isVisible: true,
    },
    {
      id: 2,
      group: 1,
      title: "Chart option builders",
      startOffsetDays: -16,
      endOffsetDays: -4,
      startTime: "10:00",
      endTime: "17:30",
      progress: 86,
      status: "active",
      isVisible: true,
    },
    {
      id: 3,
      group: 2,
      title: "Timeline data transform",
      startOffsetDays: -24,
      endOffsetDays: -10,
      startTime: "13:00",
      endTime: "16:30",
      progress: 100,
      status: "done",
      isVisible: true,
    },
    {
      id: 4,
      group: 2,
      title: "Gantt interaction controls",
      startOffsetDays: -7,
      endOffsetDays: 9,
      startTime: "09:00",
      endTime: "15:30",
      progress: 72,
      status: "active",
      isVisible: true,
    },
    {
      id: 5,
      group: 3,
      title: "Custom data grid",
      startOffsetDays: 3,
      endOffsetDays: 18,
      startTime: "11:00",
      endTime: "18:30",
      progress: 42,
      status: "planned",
      isVisible: true,
    },
    {
      id: 6,
      group: 4,
      title: "Portfolio documentation",
      startOffsetDays: -2,
      endOffsetDays: 12,
      startTime: "14:00",
      endTime: "17:00",
      progress: 58,
      status: "active",
      isVisible: true,
    },
  ];

  return items.map(
    ({ startOffsetDays, endOffsetDays, startTime, endTime, ...item }) => ({
      ...item,
      startDate: toRelativeDateTime(baseDate, startOffsetDays, startTime),
      endDate: toRelativeDateTime(baseDate, endOffsetDays, endTime),
    }),
  );
};

export const dashboardPayload: DashboardPayload = {
  monthlyMetrics: [
    { month: "Jan", revenue: 82, activeUsers: 34, conversionRate: 6.1 },
    { month: "Feb", revenue: 91, activeUsers: 38, conversionRate: 6.4 },
    { month: "Mar", revenue: 104, activeUsers: 43, conversionRate: 7.2 },
    { month: "Apr", revenue: 99, activeUsers: 47, conversionRate: 7.0 },
    { month: "May", revenue: 116, activeUsers: 53, conversionRate: 7.8 },
    { month: "Jun", revenue: 129, activeUsers: 58, conversionRate: 8.1 },
  ],
  chartImplementationMetrics: [
    { month: "Jan", current: 76, previous: 62, reviewScore: 71 },
    { month: "Feb", current: 84, previous: 67, reviewScore: 74 },
    { month: "Mar", current: 93, previous: 72, reviewScore: 81 },
    { month: "Apr", current: 101, previous: 80, reviewScore: 83 },
    { month: "May", current: 118, previous: 86, reviewScore: 88 },
    { month: "Jun", current: 126, previous: 94, reviewScore: 91 },
    { month: "Jul", current: 132, previous: 99, reviewScore: 93 },
  ],
  chartCapabilityTree: [
    {
      name: "ECharts",
      value: 64,
      children: [
        { name: "Bar + Line", value: 18 },
        { name: "Pie / Stack Pie", value: 12 },
        { name: "Treemap", value: 9 },
        { name: "Sankey", value: 10 },
        { name: "Box plot / KM", value: 15 },
      ],
    },
    {
      name: "Markup Chart",
      value: 22,
      children: [
        { name: "Heatmap", value: 8 },
        { name: "Custom legend", value: 7 },
        { name: "Label layout", value: 7 },
      ],
    },
    {
      name: "Shared UX",
      value: 28,
      children: [
        { name: "Tooltip", value: 8 },
        { name: "Resize", value: 6 },
        { name: "Legend state", value: 7 },
        { name: "i18n labels", value: 7 },
      ],
    },
  ],
  chartCategoryShare: [
    { name: "ECharts based", value: 58 },
    { name: "Markup based", value: 18 },
    { name: "Shared handling", value: 16 },
    { name: "Troubleshooting", value: 8 },
  ],
  qualityPoints: [
    {
      feature: "Chart builder",
      team: "Platform",
      cycleTimeDays: 7,
      defectRate: 1.8,
      complexity: 8,
    },
    {
      feature: "Data table",
      team: "Product",
      cycleTimeDays: 5,
      defectRate: 2.4,
      complexity: 5,
    },
    {
      feature: "Timeline view",
      team: "Product",
      cycleTimeDays: 9,
      defectRate: 2.1,
      complexity: 7,
    },
    {
      feature: "Mock API",
      team: "Platform",
      cycleTimeDays: 4,
      defectRate: 1.1,
      complexity: 4,
    },
    {
      feature: "i18n copy",
      team: "Experience",
      cycleTimeDays: 3,
      defectRate: 0.9,
      complexity: 3,
    },
    {
      feature: "Responsive QA",
      team: "Experience",
      cycleTimeDays: 6,
      defectRate: 1.5,
      complexity: 6,
    },
    {
      feature: "Sankey flow",
      team: "Platform",
      cycleTimeDays: 8,
      defectRate: 1.7,
      complexity: 8,
    },
  ],
  genderBoxPlotMetrics: [
    {
      group: "20s",
      gender: "Male",
      values: [
        14, 18, 25, 26, 29, 54, 55, 56, 58, 60, 61, 62, 64, 65, 66, 68, 69, 70,
        72, 74, 76, 94,
      ],
    },
    {
      group: "20s",
      gender: "Female",
      values: [
        26, 27, 52, 54, 55, 56, 58, 60, 62, 64, 65, 66, 68, 70, 72, 74, 76, 92,
      ],
    },
    {
      group: "30s",
      gender: "Male",
      values: [46, 48, 50, 53, 56, 58, 60, 62, 65, 67, 69, 72, 75, 78, 82, 86],
    },
    {
      group: "30s",
      gender: "Female",
      values: [31, 34, 55, 56, 58, 60, 61, 63, 64, 66, 68, 70, 72, 74, 76],
    },
    {
      group: "40s",
      gender: "Male",
      values: [
        40, 42, 44, 58, 60, 62, 64, 66, 67, 69, 70, 72, 74, 76, 78, 80, 82, 84,
        111,
      ],
    },
    {
      group: "40s",
      gender: "Female",
      values: [45, 48, 50, 54, 56, 58, 60, 62, 64, 66, 68, 70, 72, 74, 78],
    },
    {
      group: "50s",
      gender: "Male",
      values: [34, 36, 38, 42, 45, 48, 52, 55, 58, 62, 66, 70, 74, 78, 82],
    },
    {
      group: "50s",
      gender: "Female",
      values: [
        28, 33, 36, 40, 42, 44, 56, 58, 60, 61, 62, 64, 65, 66, 68, 70, 72, 74,
        97, 104,
      ],
    },
  ],
  workflow: {
    nodes: [
      { name: "A" },
      { name: "C" },
      { name: "D2" },
      { name: "A-1" },
      { name: "C-1" },
      { name: "D2-1" },
    ],
    links: [
      { source: "A", target: "A-1", value: 1346 },
      { source: "C", target: "A-1", value: 270 },
      { source: "D2", target: "A-1", value: 61 },
      { source: "A", target: "C-1", value: 249 },
      { source: "C", target: "C-1", value: 368 },
      { source: "D2", target: "C-1", value: 68 },
      { source: "A", target: "D2-1", value: 82 },
      { source: "C", target: "D2-1", value: 92 },
      { source: "D2", target: "D2-1", value: 366 },
    ],
  },
  roadmapGroups: [
    { id: 1, title: "Chart System", owner: "Frontend" },
    { id: 2, title: "Timeline", owner: "Frontend" },
    { id: 3, title: "Data Grid", owner: "Frontend" },
    { id: 4, title: "i18n / Docs", owner: "Frontend" },
  ],
  roadmapItems: buildRoadmapItems(),
  deliveryRows: [
    {
      id: "D-1042",
      project: "Revenue insight dashboard",
      domain: "Visualization",
      status: "Stable",
      leadTimeDays: 8,
      chartCoverage: 94,
      apiContract: "typed mock response",
      updatedAt: "2026-04-18",
    },
    {
      id: "D-1043",
      project: "Operation flow analytics",
      domain: "ECharts Sankey",
      status: "Improving",
      leadTimeDays: 11,
      chartCoverage: 87,
      apiContract: "builder input DTO",
      updatedAt: "2026-04-21",
    },
    {
      id: "D-1044",
      project: "Delivery roadmap timeline",
      domain: "Timeline",
      status: "Stable",
      leadTimeDays: 9,
      chartCoverage: 76,
      apiContract: "roadmap item schema",
      updatedAt: "2026-04-23",
    },
    {
      id: "D-1045",
      project: "Portfolio data table",
      domain: "Data Grid",
      status: "Watch",
      leadTimeDays: 13,
      chartCoverage: 68,
      apiContract: "table row schema",
      updatedAt: "2026-04-24",
    },
  ],
  portfolioGridRows: [
    {
      id: "PG-001",
      capability: "ECharts chart archive",
      category: "Visualization",
      owner: "Frontend",
      status: "Stable",
      coverage: 92,
      updatedAt: "2026-04-22",
      children: [
        {
          id: "PG-001-1",
          name: "Bar + line option builder",
          owner: "Frontend",
          status: "Stable",
          impact: "High",
          updatedAt: "2026-04-22",
          notes: "Separates data, option generation, and chart lifecycle.",
        },
        {
          id: "PG-001-2",
          name: "Treemap and pie examples",
          owner: "Frontend",
          status: "Improving",
          impact: "Medium",
          updatedAt: "2026-04-21",
          notes:
            "Uses dummy portfolio categories instead of real service data.",
        },
      ],
    },
    {
      id: "PG-002",
      capability: "Clinical timeline demo",
      category: "Timeline",
      owner: "Frontend",
      status: "Improving",
      coverage: 84,
      updatedAt: "2026-04-24",
      children: [
        {
          id: "PG-002-1",
          name: "Group and item transform layer",
          owner: "Frontend",
          status: "Stable",
          impact: "High",
          updatedAt: "2026-04-23",
          notes:
            "Keeps roadmap data separate from react-calendar-timeline props.",
        },
        {
          id: "PG-002-2",
          name: "Zoom and resize handlers",
          owner: "Frontend",
          status: "Improving",
          impact: "High",
          updatedAt: "2026-04-24",
          notes:
            "Supports controlled visible range plus item move and resize updates.",
        },
      ],
    },
    {
      id: "PG-003",
      capability: "Custom data grid",
      category: "Data Grid",
      owner: "Frontend",
      status: "Review",
      coverage: 78,
      updatedAt: "2026-04-25",
      children: [
        {
          id: "PG-003-1",
          name: "Accordion rows",
          owner: "Frontend",
          status: "Stable",
          impact: "Medium",
          updatedAt: "2026-04-25",
          notes:
            "Shows parent and child records with independent row rendering.",
        },
        {
          id: "PG-003-2",
          name: "Search, filter, and selection",
          owner: "Frontend",
          status: "Review",
          impact: "High",
          updatedAt: "2026-04-25",
          notes:
            "Models repeated operational grid interactions without RealGrid data.",
        },
      ],
    },
    {
      id: "PG-004",
      capability: "i18n structure",
      category: "Maintainability",
      owner: "Frontend",
      status: "Stable",
      coverage: 81,
      updatedAt: "2026-04-19",
      children: [
        {
          id: "PG-004-1",
          name: "Dictionary based copy",
          owner: "Frontend",
          status: "Stable",
          impact: "Medium",
          updatedAt: "2026-04-19",
          notes:
            "Keeps copy outside of feature components for easier extension.",
        },
      ],
    },
  ],
};
